import { useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';

import type { TutorResponse } from '@/api/generated/model';
import {
  getTutoresControllerListarQueryKey,
  useTutoresControllerExcluir,
  useTutoresControllerListar,
} from '@/api/generated/tutores/tutores';
import { ApiError } from '@/api/http-client';
import { TutorFormDialogo } from '@/features/tutores/components/TutorFormDialogo';
import { CANAIS, ROTULO_CANAL, rotuloDoCanal } from '@/features/tutores/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { formatarParaExibicao } from '@/shared/lib/telefone';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Tutores (F3-1) — a primeira tela de dado clínico.
 *
 * ⚠️ Sem filtro de ambiente, e é a diferença que importa em relação às telas de
 * administração: o escopo NÃO é escolha da interface. O servidor resolve o
 * tenant pela sessão e só devolve o que é do ambiente de quem pediu — não há
 * parâmetro para pedir outro, nem para o super_admin.
 */
export function TutoresPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [canal, setCanal] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<TutorResponse | undefined>();
  const [aExcluir, setAExcluir] = useState<TutorResponse | null>(null);

  const { data, isPending, isError, refetch } = useTutoresControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(canal !== TODOS ? { canalPreferido: canal as (typeof CANAIS)[number] } : {}),
  });

  const excluir = useTutoresControllerExcluir();

  const podeCriar = pode('tutores.criar');
  const podeEditar = pode('tutores.editar');
  const podeExcluir = pode('tutores.excluir');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function confirmarExclusao(tutor: TutorResponse) {
    try {
      await excluir.mutateAsync({ id: tutor.id });
      await queryClient.invalidateQueries({ queryKey: getTutoresControllerListarQueryKey() });
      toast.success(`${tutor.nome} foi excluído.`);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir o responsável.');
    } finally {
      setAExcluir(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome, documento, e-mail ou telefone..."
            className="sm:max-w-xs"
          />

          <Select value={canal} onValueChange={(v) => aoFiltrar(() => setCanal(v))}>
            <SelectTrigger className="sm:w-48" aria-label="Filtrar por canal preferido">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os canais</SelectItem>
              {CANAIS.map((valor) => (
                <SelectItem key={valor} value={valor}>
                  {ROTULO_CANAL[valor] ?? valor}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {podeCriar && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Novo responsável
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Responsáveis</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Users}
            titulo="Nenhum responsável encontrado"
            descricao={
              busca || canal !== TODOS
                ? 'Tente outro termo ou limpe os filtros.'
                : 'Cadastre o primeiro responsável para começar.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead className="text-right">Pacientes</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((tutor) => (
                    <TableRow key={tutor.id}>
                      <TableCell>
                        <span className="font-medium text-foreground">{tutor.nome}</span>
                        {tutor.documento && (
                          <p className="text-xs text-muted-foreground mt-0.5">{tutor.documento}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Formato internacional na listagem: aqui não há
                            bandeira ao lado dizendo de que país é o número. */}
                        <span className="text-foreground">
                          {formatarParaExibicao(tutor.telefone) || '—'}
                        </span>
                        {tutor.email && (
                          <p className="text-xs text-muted-foreground mt-0.5">{tutor.email}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {tutor.canalPreferido ? (
                          <Badge variant="outline">{rotuloDoCanal(tutor.canalPreferido)}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium text-brand">
                        {tutor.totalPacientes}
                      </TableCell>
                      <TableCell>
                        {(podeEditar || podeExcluir) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label={`Ações de ${tutor.nome}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {podeEditar && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEmEdicao(tutor);
                                    setFormAberto(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                  Editar
                                </DropdownMenuItem>
                              )}

                              {podeExcluir && (
                                <DropdownMenuItem onClick={() => setAExcluir(tutor)}>
                                  <Trash2 className="w-4 h-4" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <TutorFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { tutor: emEdicao } : {})}
      />

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {aExcluir?.nome}?</AlertDialogTitle>
            {/* O número importa: sem ele "excluir" parece uma ação isolada, e
                não é — o responsável some das listagens e os pacientes ficam
                sem responsável visível. */}
            <AlertDialogDescription>
              {aExcluir && aExcluir.totalPacientes > 0
                ? `${aExcluir.totalPacientes} paciente(s) ficam sem responsável na listagem. O histórico clínico é preservado e o vínculo pode ser refeito.`
                : 'Este responsável não tem pacientes vinculados. O registro sai das listagens, mas continua no histórico.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => aExcluir && void confirmarExclusao(aExcluir)}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
